import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Footer from './components/Footer';
import AdminNavigation from './components/AdminNavigation';
import PullToRefresh from './components/PullToRefresh';
import MobileTitleBar from './components/MobileTitleBar';
import ShareContentModal from './components/chapters/ShareContentModal';
import AudioPlayerOverlay from './components/chapters/AudioPlayerOverlay';
import ProcessingProgressBar from './components/ProcessingProgressBar';
import DownloadedIndicator from './components/DownloadedIndicator';
import * as audioCache from './utils/offline/audioCache';
import { useOnlineOnly } from './components/OfflineHint';
import PhaseTriggerForm from './components/chapters/PhaseTriggerForm';
import useProgressPolling from './utils/useProgressPolling';
import { deriveOverallPercent } from './utils/progressMath';
import useNotifications from './utils/useNotifications';
import { getApiUrl } from './config';
import * as repository from './utils/offline/repository';
import { getAccessToken, getCachedUserId, logout } from './utils/auth';
import { getMySettings } from './utils/apiUserSettings';
import {
  getPlayStates,
  setPlayState,
} from './utils/playedChapters';
import { readSnapshot } from './utils/apiPlayedChapters';
import { getPlaybackSyncCoalescer } from './utils/playbackSyncBootstrap';
import ChapterProgressBar from './components/chapters/ChapterProgressBar';
import {
  setupMediaSession,
  clearMediaSession,
  setMediaSessionPlaybackState,
} from './utils/mediaSession';
import {
  getAdjacentChapter,
  getNextChapter,
  sortChaptersByOrder,
  formatDuration,
  formatHms,
} from './utils/chapterPlayback';

const ProcessingDetailOverlay = lazy(() => import('./components/ProcessingDetailOverlay'));

// Phase 1 (extraction) is in progress while any file has file_status
// not_started or running.
function isPhase1InProgress(snapshot) {
  if (!Array.isArray(snapshot?.files)) return false;
  return snapshot.files.some(
    (f) => f.file_status === 'not_started' || f.file_status === 'running',
  );
}

// "Actively processing" drives progress-bar visibility on the Chapters page.
// True during Phase 1 extraction as well as Phase 2/3, so the bar is visible
// even before any chapters exist. Falls back to the pre-snapshot guard until
// the first poll resolves.
function deriveIsActivelyProcessing(snapshot, preSnapshotProcessing, chaptersComplete, chapters) {
  if (!snapshot) return preSnapshotProcessing;
  const stateActive =
    snapshot.audio_state === 'in_progress' ||
    snapshot.blitz_state === 'in_progress' ||
    isPhase1InProgress(snapshot);
  if (!stateActive) return false;
  // The aggregate state flags lag behind the counters, so a finished run can
  // park the bar at 100%. Hide it only when the counters AND the chapter list
  // both say there is nothing left — the counters alone can miss a chapter a
  // file has not counted yet.
  return !(chaptersComplete && deriveOverallPercent(snapshot.files, chapters) >= 100);
}

// A stable string that changes whenever any file's status or chapter counters
// change. Used to decide when to re-fetch the chapter list so newly extracted /
// processed chapters appear without a manual page refresh.
function buildProgressSignature(snapshot) {
  if (!Array.isArray(snapshot?.files)) return '';
  return snapshot.files
    .map(
      (f) =>
        `${f.file_id}:${f.file_status}:${f.audio_status}:${f.blitz_status}:${f.chapter_count || 0}:${f.processed_chapter_count || 0}:${f.blitzed_chapter_count || 0}`,
    )
    .join('|');
}

// Resolution order: ProjectDetailResponse.auto_processing (if non-null) -> UserSettings.automatic_processing
function resolveAutoProcessing(projectAutoProcessing, userAutomaticProcessing) {
  if (projectAutoProcessing !== null && projectAutoProcessing !== undefined) {
    return !!projectAutoProcessing;
  }
  if (userAutomaticProcessing !== null && userAutomaticProcessing !== undefined) {
    return !!userAutomaticProcessing;
  }
  return true;
}

function Chapters() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContentShareModal, setShowContentShareModal] = useState(false);
  const [shareContentId, setShareContentId] = useState(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [downloadedChapterIds, setDownloadedChapterIds] = useState(new Set());
  const [offlineChapterNotice, setOfflineChapterNotice] = useState(null);
  const offline = useOnlineOnly();
  const [currentAudio, setCurrentAudio] = useState({ url: '', title: '', summary: '', sourceUrl: '', autoPlay: false, chapterId: null, chapterOrder: null });
  const [playStates, setPlayStates] = useState(() => {
    const snap = readSnapshot(projectId);
    return snap && snap.states ? { ...snap.states } : {};
  });
  const playedChapterIds = useMemo(() => {
    const set = new Set();
    for (const id of Object.keys(playStates)) {
      if (playStates[id] === -1) set.add(id);
    }
    return set;
  }, [playStates]);
  const inProgressMap = useMemo(() => {
    const map = {};
    for (const id of Object.keys(playStates)) {
      const v = playStates[id];
      if (Number.isInteger(v) && v > 0) map[id] = v;
    }
    return map;
  }, [playStates]);
  // Chapters in project reading order, numbered sequentially across files:
  // file 1's last chapter is followed by file 2's first, with no restart at 1.
  const sortedChapters = useMemo(() => sortChaptersByOrder(chapters), [chapters]);
  const displayOrder = useMemo(() => {
    const map = {};
    sortedChapters.forEach((c, i) => { map[c.chapter_id] = i + 1; });
    return map;
  }, [sortedChapters]);
  // Project totals for the footer row: summed audio length, and how much of it
  // has been listened to (a played chapter counts in full, an in-progress one
  // counts up to its stored resume position).
  const totals = useMemo(() => {
    let total = 0;
    let listened = 0;
    for (const c of chapters) {
      const len = Number(c.length) > 0 ? Math.floor(c.length) : 0;
      total += len;
      const state = playStates[c.chapter_id];
      if (state === -1) listened += len;
      else if (Number.isInteger(state) && state > 0) listened += Math.min(state, len);
    }
    return { total, listened, percent: total > 0 ? Math.round((listened / total) * 100) : 0 };
  }, [chapters, playStates]);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [highlightedChapterId, setHighlightedChapterId] = useState(null);
  const chapterRowRefs = useRef({});
  const titleRef = useRef(null);
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const playStatesRef = useRef(playStates);
  useEffect(() => { playStatesRef.current = playStates; }, [playStates]);
  const lastSeenTimeRef = useRef(0);
  const markedTerminalRef = useRef(new Set());

  const isOwner = !!project?.owner_id && project.owner_id === getCachedUserId();

  const {
    isSupported: notificationsSupported,
    requestPermission: requestNotificationPermission,
    sendNotification: sendFirstChapterNotification
  } = useNotifications(projectId);

  const openChapterPlayer = (chapter, autoPlay = false) => {
    const audioUrl = getApiUrl(`projects/${projectId}/chapters/${chapter.chapter_id}.mp3`);
    setCurrentAudio({
      url: audioUrl,
      title: chapter.title || `Chapter ${displayOrder[chapter.chapter_id]}`,
      summary: chapter.summary || '',
      // Only set for a chapter derived from a link; absent for uploads and
      // text written directly, and never a placeholder (spec 024 FR-022).
      sourceUrl: chapter.source_url || '',
      autoPlay,
      chapterId: chapter.chapter_id,
      chapterOrder: displayOrder[chapter.chapter_id]
    });
    setShowAudioPlayer(true);
    window.location.hash = chapter.chapter_id;
  };

  /**
   * Offline, a chapter that was never downloaded cannot play. Say so (FR-005 /
   * US1 scenario 4) rather than opening a player that stalls on a request the
   * device cannot make.
   */
  const blockedOffline = (chapter) =>
    navigator.onLine === false && !downloadedChapterIds.has(chapter.chapter_id);

  const guardPlayback = (chapter) => {
    if (!isChapterPlayable(chapter)) return false;
    if (blockedOffline(chapter)) {
      setOfflineChapterNotice(chapter.chapter_id);
      return false;
    }
    setOfflineChapterNotice(null);
    return true;
  };

  const handleRowClick = (chapter) => {
    if (!guardPlayback(chapter)) return;
    openChapterPlayer(chapter, false);
  };

  const handlePlayClick = (e, chapter) => {
    e.preventDefault();
    e.stopPropagation();
    if (!guardPlayback(chapter)) return;
    openChapterPlayer(chapter, true);
  };

  // Which chapters this device actually holds — distinct from which chapters
  // the server has audio for (FR-005).
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const rows = (await audioCache.listDownloads()) || [];
      if (cancelled) return;
      setDownloadedChapterIds(
        new Set(rows.filter((r) => r.projectId === projectId && r.state === audioCache.READY)
          .map((r) => r.chapterId)),
      );
    };
    refresh();
    const unsubscribe = audioCache.subscribe(refresh);
    return () => {
      cancelled = true;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [projectId]);

  const handleClosePlayer = () => {
    flushCurrentChapterPosition();
    setShowAudioPlayer(false);
    window.history.pushState(null, '', window.location.pathname + window.location.search);
  };

  const markPlayedLocal = (chapterId) => {
    setPlayStates((prev) => ({ ...prev, [chapterId]: -1 }));
    markedTerminalRef.current.add(chapterId);
    setPlayState(projectId, chapterId, -1);
  };

  const markUnplayedLocal = (chapterId) => {
    setPlayStates((prev) => {
      const next = { ...prev };
      delete next[chapterId];
      return next;
    });
    markedTerminalRef.current.add(chapterId);
    setPlayState(projectId, chapterId, 0);
  };

  const markChapterPlayedAndAdvance = (autoPlayNext) => {
    if (!chapters || !currentAudio.chapterId) return;
    const finishedChapterId = currentAudio.chapterId;
    markPlayedLocal(finishedChapterId);
    const nextChapter = getNextChapter(chapters, finishedChapterId);
    if (nextChapter) {
      openChapterPlayer(nextChapter, autoPlayNext);
    }
  };

  const handleAudioEnded = () => {
    markChapterPlayedAndAdvance(true);
  };

  const handleMarkAsPlayedClick = () => {
    const audio = audioRef.current;
    const isPlaying = audio && !audio.paused && !audio.ended;
    if (isPlaying) {
      markChapterPlayedAndAdvance(true);
      return;
    }
    if (!chapters || !currentAudio.chapterId) return;
    markPlayedLocal(currentAudio.chapterId);
    handleClosePlayer();
  };

  const handleMarkAsUnplayedClick = () => {
    if (!currentAudio.chapterId) return;
    markUnplayedLocal(currentAudio.chapterId);
  };

  const goToAdjacentChapter = (direction) => {
    if (!chapters || !currentAudio.chapterId) return;
    const adjacent = getAdjacentChapter(chapters, currentAudio.chapterId, direction);
    if (adjacent) openChapterPlayer(adjacent, true);
  };

  const handleShareChapterClick = (chapterId) => {
    setShareContentId(chapterId);
    setShowContentShareModal(true);
  };

  const handleContentShareModalClose = () => {
    setShowContentShareModal(false);
    setShareContentId(null);
  };

  /** Read this project from the device. Returns false if nothing is cached. */
  const readCached = useCallback(async () => {
    try {
      const [cachedProject, cachedChapters] = await Promise.all([
        repository.getProject(projectId),
        repository.getChapters(projectId),
      ]);
      if (!cachedProject) return false;
      setProject(cachedProject);
      setChapters(cachedChapters);
      return true;
    } catch (e) {
      return false; // no account bound yet, or the store is unavailable
    }
  }, [projectId]);

  /**
   * Render what the device already holds, then refresh from the server.
   *
   * With the project on screen from cache, a network failure is not an error
   * (FR-001, FR-006) — the chapter list stays browsable and the offline banner
   * explains that it may be stale.
   */
  const fetchProjectData = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setError('No access token found. Please login first.');
      setLoading(false);
      logout();
      return;
    }

    const hadCache = await readCached();
    if (hadCache) {
      setError('');
      setLoading(false);
    }

    try {
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const [projectRes, chaptersRes, settingsData] = await Promise.all([
        axios.get(getApiUrl(`projects/${projectId}`), { headers }),
        axios.get(getApiUrl(`projects/${projectId}/chapters`), { headers }),
        getMySettings().catch(() => null),
      ]);
      // Passed to the cache unmodified: a response with no `chapters` field
      // must not be reconciled as "this project has no chapters" (FR-037).
      const rawChapters = chaptersRes.data.chapters;
      const freshChapters = rawChapters || [];
      setProject(projectRes.data);
      setChapters(freshChapters);
      if (settingsData) {
        setUserSettings(settingsData);
      }
      setError('');
      // Best effort, and deliberately isolated: a storage failure must never
      // be reported to the user as a problem loading the project.
      Promise.resolve()
        .then(() => Promise.all([
          repository.cacheProjects([{ id: projectId, ...projectRes.data }]),
          repository.cacheChapters(projectId, rawChapters),
        ]))
        .catch((e) => console.warn('Chapters: cache write failed', e));
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setError('Unauthorized: Please login again.');
          logout();
        } else if (err.response.status === 404) {
          setError('Project not found.');
        } else {
          setError(`Error: ${err.response.data.message || err.response.statusText}`);
        }
      } else if (hadCache) {
        setError(''); // offline, but the device has this project
      } else if (err.request) {
        setError('Network error: Could not connect to server');
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, readCached]);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    await fetchProjectData();
  }, [fetchProjectData]);

  // Set true the moment the owner triggers audio generation, so the page starts
  // polling and shows the progress bar immediately — before the backend has had
  // time to flip audio_status to 'running'. Cleared once the snapshot reports the
  // audio phase has reached a terminal state.
  const [processingActive, setProcessingActive] = useState(false);

  // Progress polling: active while extraction is ongoing or audio/blitz is
  // running or pending (queued but not yet picked up by a worker).
  const preSnapshotProcessing = project?.files?.some(
    (f) =>
      f.file_status !== 'done' ||
      f.audio_status === 'running' ||
      f.audio_status === 'pending' ||
      f.blitz_status === 'running' ||
      f.blitz_status === 'pending',
  ) ?? false;
  // Every loaded chapter has both its narration and its key points.
  const chaptersComplete =
    chapters.length > 0 && chapters.every((c) => c.has_chapter_audio && c.has_key_points);
  // With auto-processing on, a chapter without audio is work the pipeline still
  // owes us. With it off, that is the expected resting state (the owner gets the
  // manual "Generate audio" trigger instead), so it must not raise the bar.
  const autoProcessing = resolveAutoProcessing(
    project?.auto_processing,
    userSettings?.automatic_processing,
  );
  const chaptersPending = autoProcessing && chapters.some((c) => !c.has_chapter_audio);

  const handleProgressDone = useCallback(() => {
    fetchProjectData();
  }, [fetchProjectData]);
  const handleProgressAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      setError('Unauthorized: Please login again.');
      logout();
    }
  }, []);
  const pollingEnabled = !!(project && (preSnapshotProcessing || processingActive || chaptersPending));
  const { snapshot: progressSnapshot } = useProgressPolling(
    projectId,
    pollingEnabled,
    {
      onDone: handleProgressDone,
      onError: handleProgressAuthError,
      // Stop when audio has reached a terminal state. Blitz may not be
      // triggered at all (blitz_state stays 'not_started'), so requiring it
      // to be 'done' would keep polling forever in the audio-only case.
      isDone: (snap) =>
        (snap?.audio_state === 'done' || snap?.audio_state === 'failed') &&
        (snap?.blitz_state === 'done' || snap?.blitz_state === 'failed' || snap?.blitz_state === 'not_started'),
    },
  );

  const isActivelyProcessing =
    processingActive ||
    chaptersPending ||
    deriveIsActivelyProcessing(progressSnapshot, preSnapshotProcessing, chaptersComplete, chapters);

  // Once the snapshot shows the audio phase has finished (or failed), drop the
  // optimistic just-triggered flag so polling and the bar can settle.
  useEffect(() => {
    if (!processingActive) return;
    if (progressSnapshot?.audio_state === 'done' || progressSnapshot?.audio_state === 'failed') {
      setProcessingActive(false);
    }
  }, [processingActive, progressSnapshot]);

  // Start polling + refresh immediately when the owner triggers audio generation
  // (202 accepted) or when the backend reports a run already in progress (409).
  const handleProcessTriggered = useCallback((result) => {
    if (result?.kind === 'accepted' || result?.kind === 'conflict') {
      setProcessingActive(true);
      fetchProjectData();
    }
  }, [fetchProjectData]);

  // Whether individual chapters have audio (from chapters list)
  const chapterAudioReady = useMemo(() => {
    const set = new Set();
    chapters.forEach((c) => {
      if (c?.has_chapter_audio) set.add(c.chapter_id);
    });
    return set;
  }, [chapters]);

  // A chapter is playable only when it actually has narration audio
  // (`has_chapter_audio`). It is NOT gated on whether the project is actively
  // processing — chapters can exist without audio (e.g. extraction done but
  // audio not yet generated), and those must show "Audio pending", not Play.
  const isChapterPlayable = useCallback(
    (chapter) => chapterAudioReady.has(chapter.chapter_id),
    [chapterAudioReady],
  );

  // Auto-refresh the chapter list whenever the progress snapshot reports a
  // per-file change (new chapters from extraction, or audio / key points
  // becoming available). Intentionally NOT gated on "actively processing" so
  // the final batch is fetched even as the project transitions to done —
  // otherwise newly extracted/processed chapters only appear after a manual
  // page refresh.
  const snapChapterCountTotal = useMemo(() => {
    if (!Array.isArray(progressSnapshot?.files)) return 0;
    return progressSnapshot.files.reduce((s, f) => s + (f.chapter_count || 0), 0);
  }, [progressSnapshot]);
  const snapProgressSignature = useMemo(
    () => buildProgressSignature(progressSnapshot),
    [progressSnapshot],
  );
  const lastProgressSigRef = useRef(null);
  useEffect(() => {
    if (!progressSnapshot) return;
    if (lastProgressSigRef.current === snapProgressSignature) return;
    const isFirstSnapshot = lastProgressSigRef.current === null;
    lastProgressSigRef.current = snapProgressSignature;
    // On the first snapshot, only refetch if it already disagrees with the
    // chapters loaded on mount; on every later change, always refetch.
    if (isFirstSnapshot && snapChapterCountTotal === chapters.length) return;
    fetchProjectData();
  }, [progressSnapshot, snapProgressSignature, snapChapterCountTotal, chapters.length, fetchProjectData]);

  useEffect(() => {
    if (projectId) {
      fetchProjectData();
    } else {
      setError('No project ID provided');
      setLoading(false);
    }
  }, [projectId, fetchProjectData]);

  useEffect(() => {
    if (!notificationsSupported) return;
    if (!projectId) return;
    requestNotificationPermission().catch((error) => {
      console.error('Error requesting notification permission:', error);
    });
  }, [projectId, notificationsSupported, requestNotificationPermission]);

  const prevSnapshotRef = useRef(null);
  useEffect(() => {
    if (!notificationsSupported) return;
    if (!isActivelyProcessing) return;
    if (!progressSnapshot || !Array.isArray(progressSnapshot.files) || progressSnapshot.files.length === 0) return;
    if (!project?.title) return;

    const prev = prevSnapshotRef.current;
    const curr = progressSnapshot;

    if (prev && Array.isArray(prev.files) && chapters.length > 0) {
      const prevTotal = prev.files.reduce((s, f) => s + (f.processed_chapter_count || 0), 0);
      const currTotal = curr.files.reduce((s, f) => s + (f.processed_chapter_count || 0), 0);
      if (prevTotal === 0 && currTotal > 0) {
        try {
          sendFirstChapterNotification(project.title);
        } catch (error) {
          console.error('Error sending first chapter notification:', error);
        }
      }
    }

    prevSnapshotRef.current = curr;
  }, [progressSnapshot, isActivelyProcessing, notificationsSupported, sendFirstChapterNotification, project, chapters]);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    const refresh = () => {
      getPlayStates(projectId).then((states) => {
        if (!cancelled && states && typeof states === 'object') {
          setPlayStates(states);
        }
      }).catch(() => {});
    };
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [projectId]);

  const initialAutoOpenDone = useRef(false);
  useEffect(() => {
    if (!chapters || chapters.length === 0) return;
    if (initialAutoOpenDone.current) return;
    initialAutoOpenDone.current = true;

    const openChapter = (chapter, autoPlay) => {
      const audioUrl = getApiUrl(`projects/${projectId}/chapters/${chapter.chapter_id}.mp3`);
      setCurrentAudio({
        url: audioUrl,
        title: chapter.title || `Chapter ${displayOrder[chapter.chapter_id]}`,
        summary: chapter.summary || '',
        sourceUrl: chapter.source_url || '',
        autoPlay,
        chapterId: chapter.chapter_id,
        chapterOrder: displayOrder[chapter.chapter_id]
      });
      setShowAudioPlayer(true);
      window.location.hash = chapter.chapter_id;
    };

    const hash = window.location.hash.slice(1);
    if (hash) {
      const chapter = chapters.find((ch) => ch.chapter_id === hash);
      if (chapter) {
        openChapter(chapter, false);
        return;
      }
    }

    const autoplayChapterId = searchParams.get('autoplay');
    if (autoplayChapterId) {
      const chapter = chapters.find((ch) => ch.chapter_id === autoplayChapterId);
      if (chapter) {
        openChapter(chapter, true);
        setSearchParams({}, { replace: true });
      }
    }
  }, [chapters, displayOrder, searchParams, setSearchParams, projectId]);

  const highlightDoneRef = useRef(false);
  useEffect(() => {
    if (highlightDoneRef.current) return;
    if (!chapters || chapters.length === 0) return;
    const target = searchParams.get('highlight');
    if (!target) return;
    const found = chapters.find((ch) => ch.chapter_id === target);
    if (!found) return;
    highlightDoneRef.current = true;
    setHighlightedChapterId(target);
    requestAnimationFrame(() => {
      const node = chapterRowRefs.current[target];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    const timeoutId = setTimeout(() => setHighlightedChapterId(null), 2500);
    const next = new URLSearchParams(searchParams);
    next.delete('highlight');
    setSearchParams(next, { replace: true });
    return () => clearTimeout(timeoutId);
  }, [chapters, searchParams, setSearchParams]);

  // Mobile only: on first load, jump to where the user left off — the furthest
  // chapter in reading order that has been played or started. Skipped when a
  // hash / ?highlight= / ?autoplay= already targets a specific chapter, and on
  // desktop, where the list is short enough to scan.
  const resumeScrollDoneRef = useRef(false);
  useEffect(() => {
    if (resumeScrollDoneRef.current) return;
    if (sortedChapters.length === 0) return;
    if (window.innerWidth >= 768) return;
    if (window.location.hash || searchParams.get('highlight') || searchParams.get('autoplay')) return;
    const target = sortedChapters
      .filter((c) => playedChapterIds.has(c.chapter_id) || inProgressMap[c.chapter_id])
      .pop();
    if (!target) return;
    resumeScrollDoneRef.current = true;
    requestAnimationFrame(() => {
      const node = chapterRowRefs.current[target.chapter_id];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'center' });
      }
    });
  }, [sortedChapters, playedChapterIds, inProgressMap, searchParams]);

  useEffect(() => {
    if (!showAudioPlayer || !currentAudio.chapterId) {
      clearMediaSession();
      return undefined;
    }

    return setupMediaSession({
      metadata: {
        title: currentAudio.title,
        artist: project?.title || 'Dissona',
        album: 'Dissona',
        artwork: [
          { src: '/favicon-192x192.png', sizes: '192x192', type: 'image/png' },
          { src: '/android-chrome-192x192.png', sizes: '192x192', type: 'image/png' },
          { src: '/android-chrome-512x512.png', sizes: '512x512', type: 'image/png' },
        ],
      },
      getAudio: () => audioRef.current,
      onPreviousTrack: () => goToAdjacentChapter('prev'),
      onNextTrack: () => goToAdjacentChapter('next'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showAudioPlayer,
    currentAudio.chapterId,
    currentAudio.title,
    project?.title,
  ]);

  const handleAudioPlay = () => {
    setMediaSessionPlaybackState('playing');
  };

  const flushCurrentChapterPosition = useCallback(() => {
    const audio = audioRef.current;
    const cId = currentAudio.chapterId;
    if (!cId) return;
    if (markedTerminalRef.current.has(cId)) {
      const coalescer = getPlaybackSyncCoalescer();
      if (coalescer) coalescer.flush(cId);
      return;
    }
    const tNow = audio ? Math.floor(audio.currentTime || 0) : 0;
    const tFallback = Math.floor(lastSeenTimeRef.current || 0);
    const t = tNow > 0 ? tNow : tFallback;
    if (t > 0) {
      setPlayStates((prev) => ({ ...prev, [cId]: t }));
      setPlayState(projectId, cId, t);
    }
    const coalescer = getPlaybackSyncCoalescer();
    if (coalescer) coalescer.flush(cId);
  }, [projectId, currentAudio.chapterId]);

  const handleAudioPause = () => {
    setMediaSessionPlaybackState('paused');
    flushCurrentChapterPosition();
  };

  useEffect(() => () => {
    const coalescer = getPlaybackSyncCoalescer();
    if (coalescer) coalescer.flushAll();
  }, []);

  // Seeking to the stored position is a once-per-open action. This effect also
  // re-runs on every `chapters` identity change — which a progress-poll refetch
  // causes mid-playback — and re-applying the seek then dragged the listener
  // back to whatever the 10s ticker had last saved, up to 10s of already-heard
  // audio. The guard, not the dependency list, is what keeps it to one seek:
  // the duration clamp below still needs `chapters`.
  const resumedChapterRef = useRef(null);
  useEffect(() => {
    if (!showAudioPlayer || !currentAudio.chapterId) {
      resumedChapterRef.current = null;
      return undefined;
    }
    const audio = audioRef.current;
    if (!audio) return undefined;
    if (resumedChapterRef.current === currentAudio.chapterId) return undefined;
    resumedChapterRef.current = currentAudio.chapterId;
    const resume = playStatesRef.current[currentAudio.chapterId];
    if (!Number.isInteger(resume) || resume <= 0) return undefined;
    const chapter = chapters?.find((c) => c.chapter_id === currentAudio.chapterId);
    const duration = chapter?.length;
    const target = (Number.isFinite(duration) && duration > 0)
      ? Math.min(resume, Math.max(0, Math.floor(duration) - 1))
      : resume;
    const seek = () => { try { audio.currentTime = target; } catch (_) { /* jsdom / unsupported */ } };
    if (audio.readyState >= 1) {
      seek();
      return undefined;
    }
    audio.addEventListener('loadedmetadata', seek, { once: true });
    return () => audio.removeEventListener('loadedmetadata', seek);
  }, [showAudioPlayer, currentAudio.chapterId, chapters]);

  useEffect(() => {
    if (!showAudioPlayer || !currentAudio.chapterId) return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;
    const cId = currentAudio.chapterId;
    const pId = projectId;
    lastSeenTimeRef.current = 0;
    const sendPosition = (raw) => {
      const t = Math.floor(raw || 0);
      if (t <= 0) return;
      if (markedTerminalRef.current.has(cId)) return;
      setPlayStates((prev) => ({ ...prev, [cId]: t }));
      setPlayState(pId, cId, t);
    };
    let intervalId = null;
    const startTicker = () => {
      if (intervalId == null) {
        intervalId = setInterval(() => sendPosition(audio.currentTime), 10000);
      }
    };
    const stopTicker = () => {
      if (intervalId != null) { clearInterval(intervalId); intervalId = null; }
    };
    const onPlay = () => {
      markedTerminalRef.current.delete(cId);
      startTicker();
    };
    const onPause = () => stopTicker();
    const onTimeUpdate = () => {
      if (audio.currentTime > 0) lastSeenTimeRef.current = audio.currentTime;
    };
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTimeUpdate);
    if (!audio.paused) startTicker();
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      stopTicker();
      sendPosition(lastSeenTimeRef.current);
    };
  }, [showAudioPlayer, currentAudio.chapterId, projectId]);


  // Process trigger visibility logic (FR-011)
  // Show only when:
  // - Current user is owner
  // - Project is loaded
  // - Not actively processing
  // - auto_processing resolves to false (project.auto_processing if non-null, else UserSettings.automatic_processing)
  // - Not all loaded chapters already have audio
  // - Progress snapshot audio_state is not done / in_progress
  // - At least one file has file_status === 'done' and audio_status not started / failed
  const showProcessTrigger = (() => {
    if (!isOwner) return false;
    if (!project) return false;
    if (processingActive) return false;
    if (resolveAutoProcessing(project.auto_processing, userSettings?.automatic_processing)) return false;
    // If every loaded chapter already has audio, generation is done
    if (chapters && chapters.length > 0 && chapters.every((c) => c.has_chapter_audio)) return false;
    const snap = progressSnapshot;
    if (snap) {
      // in_progress covers both pending and running files at the aggregate level
      if (snap.audio_state === 'done' || snap.audio_state === 'in_progress') return false;
    }
    const files = project.files || [];
    return files.some((f) =>
      f.file_status === 'done' &&
      (f.audio_status === 'not_started' || f.audio_status === 'failed'),
    );
  })();

  // Blitz button visibility: show when any chapter exists and process is done
  const showBlitzButton = (() => {
    if (!chapters || chapters.length === 0) return false;
    const snap = progressSnapshot;
    if (snap) return snap.audio_state === 'done' || snap.audio_state === 'failed';
    // Fallback: check project files
    const files = project?.files || [];
    return files.every((f) => f.audio_status === 'done' || f.audio_status === 'failed');
  })();

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-background-light dark:bg-background-dark">
        <AdminNavigation />
        <MobileTitleBar title={project?.title} titleRef={titleRef} />

        <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <li>
                <Link to="/dashboard" className="hover:text-primary transition-colors">
                  Dashboard
                </Link>
              </li>
              <li>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </li>
              <li className="text-gray-900 dark:text-white truncate max-w-xs sm:max-w-md">
                {project?.title || 'Chapters'}
              </li>
            </ol>
          </nav>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading chapters...</span>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              {error.includes('login') && (
                <Link
                  to="/login"
                  className="mt-2 inline-block text-sm text-red-600 dark:text-red-400 underline hover:no-underline"
                >
                  Go to Login
                </Link>
              )}
            </div>
          )}

          {!loading && !error && project && (
            <div>
              <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
                  <h1 ref={titleRef} className="text-3xl font-bold text-gray-900 dark:text-white">
                    {project.title || 'File Chapters'}
                  </h1>
                  <div className="flex items-center gap-2 -ml-2 md:ml-0">
                    {/* Add files — owner only, and only when it can work.
                        Uploading needs the server (FR-018), and unlike an
                        action button a navigation link has nothing useful to
                        say in a disabled state, so it is hidden instead. */}
                    {isOwner && !offline && (
                      <Link
                        to={`/projects/${projectId}/upload`}
                        className="inline-flex items-center p-2 text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                        title="Add files"
                        aria-label="Add files"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </Link>
                    )}

                    {/* Project settings gear */}
                    <Link
                      to={`/projects/${projectId}/settings`}
                      className="inline-flex items-center p-2 text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                      title="Project settings"
                      aria-label="Project settings"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </Link>
                  </div>
                </div>

                {isActivelyProcessing && (
                  <ProcessingProgressBar
                    ref={barRef}
                    snapshot={progressSnapshot}
                    chapters={chapters}
                    onOpenDetail={() => setOverlayOpen(true)}
                  />
                )}
                {isActivelyProcessing && overlayOpen && (
                  <Suspense fallback={null}>
                    <ProcessingDetailOverlay
                      snapshot={progressSnapshot}
                      onClose={() => {
                        setOverlayOpen(false);
                        barRef.current?.focus();
                      }}
                    />
                  </Suspense>
                )}

                {/* Owner-only manual Process trigger */}
                {showProcessTrigger && (
                  <div className="mt-3 mb-4" data-testid="process-trigger-area">
                    <PhaseTriggerForm phase="process" fileId={projectId} onSubmitted={handleProcessTriggered} />
                  </div>
                )}
              </div>

              {/* Shared-with-you banner */}
              {!isOwner && project && (
                <div className="mb-4 p-3 rounded-lg border border-primary/30 bg-primary/5 text-sm text-gray-800 dark:text-gray-200">
                  Shared with you.
                </div>
              )}

              {/* Chapters List */}
              <div className="bg-white dark:bg-background-dark/50 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Chapters ({chapters ? chapters.length : 0})
                  </h2>
                  {showBlitzButton && (
                    <Link
                      to={`/blitz/${projectId}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      Blitz
                    </Link>
                  )}
                </div>

                {!chapters || chapters.length === 0 ? (
                  <div className="px-6 py-12 text-center">
                    {(project?.files?.length === 0) && isOwner && !offline ? (
                      <>
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          No files have been added to this project yet.
                        </p>
                        <Link
                          to={`/projects/${projectId}/upload`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add files
                        </Link>
                      </>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">
                        {isActivelyProcessing
                          ? 'Chapters will appear here as they are extracted.'
                          : 'No chapters found for this project'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedChapters.map((chapter) => (
                      <div
                        key={chapter.chapter_id}
                        ref={(el) => { chapterRowRefs.current[chapter.chapter_id] = el; }}
                        data-testid={`chapter-row-${chapter.chapter_id}`}
                        className={`px-6 py-4 transition-colors ${
                          isChapterPlayable(chapter)
                            ? 'hover:bg-gray-50 dark:hover:bg-gray-800/30 cursor-pointer'
                            : 'cursor-default'
                        } ${
                          highlightedChapterId === chapter.chapter_id
                            ? 'ring-2 ring-primary ring-offset-2 ring-offset-background-light dark:ring-offset-background-dark bg-primary/5'
                            : ''
                        }`}
                        onClick={() => handleRowClick(chapter)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                              Chapter {displayOrder[chapter.chapter_id]}
                            </span>
                            <span className="inline-flex items-center gap-2">
                              {playedChapterIds.has(chapter.chapter_id) && (
                                <svg
                                  className="w-4 h-4 text-green-600 dark:text-green-500 flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-label="Played"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                              <h3
                                className={`text-lg font-medium mb-1 ${
                                  playedChapterIds.has(chapter.chapter_id)
                                    ? 'text-gray-400 dark:text-gray-500'
                                    : 'text-gray-900 dark:text-white'
                                }`}
                              >
                                {chapter.title}
                              </h3>
                            </span>
                            <ChapterProgressBar
                              resumeSeconds={inProgressMap[chapter.chapter_id]}
                              durationSeconds={chapter.length}
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            {offlineChapterNotice === chapter.chapter_id && (
                              <span role="note" className="text-xs text-gray-500 dark:text-gray-400">
                                Not downloaded — connect to play this chapter.
                              </span>
                            )}
                            <DownloadedIndicator chapter={chapter} />
                            {chapter.length != null && chapter.length > 0 && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                                <svg
                                  className="w-3 h-3 mr-1"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                {formatDuration(chapter.length)}
                              </span>
                            )}
                            {/* Share Chapter Button — owner only; hidden on mobile (available in player overlay) */}
                            {isOwner && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShareChapterClick(chapter.chapter_id);
                                }}
                                className="hidden sm:inline-flex items-center p-2 text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                                title="Share this chapter"
                                aria-label="Share this chapter"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                              </button>
                            )}
                            {/* Play / Audio pending — hidden on mobile (tapping the row opens the player overlay) */}
                            {isChapterPlayable(chapter) ? (
                              <a
                                href={getApiUrl(`projects/${projectId}/chapters/${chapter.chapter_id}.mp3`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden sm:inline-flex"
                                onClick={(e) => handlePlayClick(e, chapter)}
                              >
                                <button className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                  </svg>
                                  Play
                                </button>
                              </a>
                            ) : (
                              <span
                                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                                title="Audio is still being generated"
                                aria-label="Audio pending"
                              >
                                <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                Audio pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {chapters && chapters.length > 1 && (
                  <div
                    data-testid="chapters-totals"
                    className="px-6 py-6 border-t-4 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Total length
                      </span>
                      <span
                        className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-white"
                        data-testid="chapters-total-length"
                      >
                        {formatHms(totals.total)}
                      </span>
                    </div>
                    {totals.listened > 0 && (
                      <div
                        className="mt-1 text-right text-xs text-gray-500 dark:text-gray-400 tabular-nums"
                        data-testid="chapters-listened"
                      >
                        {totals.percent}% listened
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

        {/* Content Share Modal — chapter-level sharing only (audio overlay) */}
        <ShareContentModal
          isOpen={showContentShareModal}
          contentType="chapter"
          contentId={shareContentId}
          bookId={projectId}
          showContentTypeSelector
          onClose={handleContentShareModalClose}
        />

        {/* Audio Player Overlay */}
        {showAudioPlayer && (
          <AudioPlayerOverlay
            audioRef={audioRef}
            chapterOrder={currentAudio.chapterOrder}
            title={currentAudio.title}
            summary={currentAudio.summary}
            sourceUrl={currentAudio.sourceUrl}
            audioUrl={currentAudio.url}
            autoPlay={currentAudio.autoPlay}
            isMarkedAsPlayed={playedChapterIds.has(currentAudio.chapterId)}
            isOwner={isOwner}
            onShare={() => handleShareChapterClick(currentAudio.chapterId)}
            onClose={handleClosePlayer}
            onEnded={handleAudioEnded}
            onPlay={handleAudioPlay}
            onPause={handleAudioPause}
            onMarkAsPlayed={handleMarkAsPlayedClick}
            onMarkAsUnplayed={handleMarkAsUnplayedClick}
            onPrev={() => goToAdjacentChapter('prev')}
            onNext={() => goToAdjacentChapter('next')}
            hasPrev={!!getAdjacentChapter(chapters, currentAudio.chapterId, 'prev')}
            hasNext={!!getAdjacentChapter(chapters, currentAudio.chapterId, 'next')}
          />
        )}

        <Footer desktopOnly />
      </div>
    </PullToRefresh>
  );
}

export default Chapters;
